import { useResourceKit } from '@resource-kit/core/react';
import { cn } from '../../cn';
import { EntityLink } from './EntityLink';
import { CopyButton } from './CopyButton';
import { shortenId, type IdBudget } from '@resource-kit/controllers';

// Discriminated union: at most one link target. Encoding it in the
// type system rather than as two independent optional strings means
// `IdCell` can never be misconfigured with both `linkTo` and
// `linkHref` set, which previously needed a documented "linkTo wins"
// precedence rule. New consumers get compile-time enforcement.
type IdLink =
  | { linkTo: string; linkHref?: never }
  | { linkHref: string; linkTo?: never }
  | { linkTo?: never; linkHref?: never };

type IdCellProps = {
  value: string | number | null | undefined;
  // `false`  → render the full id verbatim (with `break-all` so long
  //            ids wrap inside narrow detail rows instead of clipping).
  // `number` → shorten to a budget of N characters. Which end survives
  //            is decided by the id's shape in `shortenId` (#1978):
  //            a UUID keeps its trailing group (`…1f51e94cf39b`),
  //            anything else keeps its head (`10005153874…`). Must be
  //            ≥ 1 — values ≤ 0 are clamped to "no truncation".
  // `'uuid'` → the DETAIL-surface budget: shorten a UUID, leave every
  //            other shape whole. See `IdBudget` in `lib/short-id`.
  // The full id is always available via the native `title` tooltip
  // and the copy button regardless of this setting.
  truncate?: IdBudget;
  className?: string;
} & IdLink;

// Canonical list-mode truncation budget. Exported so feature-level
// callsites that shorten ids in *non-render* contexts (page titles,
// toast/confirm messages — places `IdCell` can't go because it
// produces a React node, not a string) can pass the same budget to
// `shortenId` and stay aligned with the list visual.
export const LIST_TRUNCATE = 12;

// Centralised renderer for entity IDs. Replaces ~14 hand-rolled
// `slice(0, N) + '...'` instances across feature modules. The primary
// UX guarantees:
//   1. The full id is *always* recoverable — via native `title`
//      tooltip on hover, and via clipboard on click.
//   2. Detail surfaces (truncate=false) show the full id verbatim
//      so operators can compare against external systems (Shopify
//      admin, Onfleet dashboard) without copy-pasting through a
//      tooltip.
//   3. Click on the copy button never bubbles into a parent row's
//      `onClick` (e.g. table row navigation) and never navigates the
//      ambient SPA link — `stopPropagation` + `preventDefault` on the
//      button, plus the structural choice of placing the button as a
//      sibling of (not a descendant of) any anchor.
//
// Schema-utils' default `id` renderer dispatches on `mode`:
// `'list' → truncate=12`, `'detail' → truncate=false`. Custom
// `field.render` callsites can override either knob.
export function IdCell(props: IdCellProps) {
  const { value, truncate = 12, className, linkTo, linkHref } = props;
  const { t } = useResourceKit();

  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground">-</span>;
  }

  const full = String(value);
  // Shape dispatch, budget clamping and the ellipsis all live in
  // `shortenId` — the same function the handful of string-context
  // callsites (dialog copy, page titles) use, so a UUID reads the same
  // everywhere. `elided` is not consumed here: the `break-all` branch
  // below keys off the *prop*, not off whether truncation happened.
  const display = shortenId(full, truncate).text;

  const textNode = linkTo ? (
    <EntityLink to={linkTo}>{display}</EntityLink>
  ) : linkHref ? (
    <a
      href={linkHref}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="text-accent hover:text-accent-hover hover:underline underline-offset-2 transition-colors"
    >
      {display}
    </a>
  ) : (
    <span className="text-muted-foreground">{display}</span>
  );

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-mono text-xs align-middle',
        // `break-all` on the two DETAIL budgets, because both can put a
        // full-length id on screen: `false` always does, and `'uuid'`
        // does for every non-UUID shape. List rows — whether they
        // actually trigger truncation or have an id short enough to fit
        // — stay on a single line so the column doesn't reflow.
        typeof truncate === 'number' ? '' : 'break-all',
        className,
      )}
      // Always populate `title` so hover reveals the full id even
      // when the visible text is identical (defends against future
      // CSS truncation in the parent container).
      title={full}
    >
      {textNode}
      <CopyButton value={full} label={t('rk.ui.copyId')} successMessage={t('rk.ui.idCopied')} />
    </span>
  );
}
